import React from 'react'
import PropTypes from 'prop-types'
import { ErrorMessage, Field } from 'formik'
import { sortBy } from 'lodash'

import { getFieldState } from 'services/Forms'
import { t } from 'translations'

import styles from './form.module.scss'

const Select = ({ addEmpty, customLabelElement, emptyPlaceholder, fieldClass, handleChange, hideDescription, inline, inputGroup, dontSort, ...props }) => {
  return (
    <Field {...props}>
      {({ field, form }) => {
        // Sort options unless already sorted
        const options = !dontSort ? sortBy(props.options, 'name') : props.options

        // Find the option based on passed initial value: if is field disabled will use this to display a select field 'lookalike' element
        const selectedOption = options.find(({ _id }) => _id === field.value)

        const fieldState = getFieldState(form, field)

        // Change the onChange handler to allow calling custom handler, and set fieldState status on an onchange event
        const defaultChangeHandler = field.onChange
        field.onChange = e => {
          // Always run default handler
          defaultChangeHandler(e)
          // If pass custom onChange handler, run it too
          if (handleChange) handleChange(e)
        }

        if (inline) {
          return (
            <select {...props} {...field} className={`merinio-inline-select ${fieldState}`}>
              {addEmpty ? <option value=""></option> : null}
              {/* Adds a default option to have nothing selected if the value is undefined */}
              {options.map(option => (
                <option key={option._id} value={option._id} disabled={option.disabled}>
                  {option.name}
                </option>
              ))}
            </select>
          )
        }

        return (
          <div className={`form-group ${fieldState} ${props.required ? 'required' : ''} ${fieldClass}`}>
            <label className="control-label">
              {t(`${props.module}.${(props.translation || props.name).toUpperCase()}.LABEL`)}
              {customLabelElement}
            </label>
            {/* Since a disable select element does not break onto the next line on small screens if the text contained
            is wider than the screen, we substitute a select element 'lookalike' div in these cases */}
            {props.disabled ? (
              // Pseudo-select element 'lookalike' when field is disabled
              <div {...props} className={styles['multiline-disabled-select-field']}>
                {selectedOption && selectedOption !== ''
                  ? // If selected option exists in the options array, display its name
                    selectedOption.name
                  : emptyPlaceholder
                  ? // If selected option does not exist and we have a placeholder when field is empty, display it
                    t(`${props.module}.${(props.translation || props.name).toUpperCase()}.EMPTY_PLACEHOLDER`)
                  : // Otherwise, display empty string
                    ''}
              </div>
            ) : (
              // Real select element when field is not disabled
              <select {...props} {...field} className={`custom-select ${fieldState}`} required={props.required}>
                {addEmpty ? <option value=""></option> : null}
                {/* Adds a default option to have nothing selected if the value is undefined */}
                {options.map(option => (
                  <option key={option._id} value={option._id} disabled={option.disabled}>
                    {option.name}
                  </option>
                ))}
              </select>
            )}
            {!hideDescription && (
              <small className="form-text text-muted">{t(`${props.module}.${(props.translation || props.name).toUpperCase()}.DESCRIPTION`)}</small>
            )}
            <ErrorMessage name={props.name} render={msg => <span className="text-danger"> {t(msg)}</span>} />
          </div>
        )
      }}
    </Field>
  )
}

export default Select

// Set the proptypes for validation
Select.propTypes = {
  module: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  required: PropTypes.bool,
  addEmpty: PropTypes.bool,
  customLabelElement: PropTypes.element
}
Select.defaultProps = {
  required: false
}
